import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, ArrowUpDown, Search, Download, Eye, Edit2, Trash2, Columns, Check, Calendar, Briefcase, DollarSign, Award, MapPin, Users, Clock, Shield, FileText } from 'lucide-react';
import { jobsAPI } from "../../services/api";
import { toast } from "react-toastify";
import { exportJobsToExcel } from "../../utils/ExportUtils";
import CreateJobModal from "./CreateJobModal";
import EditJobModal from "./EditJobModal";
import JobPreview from "./JobPreview";

const BRAND = "#1C4E6D";
const BRAND_DARK = "#153a4f";

const Jobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCustomizeView, setShowCustomizeView] = useState(false);
  const [selectedJobForEdit, setSelectedJobForEdit] = useState(null);
  const [selectedJobForPreview, setSelectedJobForPreview] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // All available columns with icons and categories
  const allColumns = [
    // Basic Information
    { key: 'jobCode', label: 'Job Code', icon: <FileText size={14} />, category: 'Basic', required: true },
    { key: 'jobTitle', label: 'Job Title', icon: <Briefcase size={14} />, category: 'Basic', required: true },
    { key: 'client', label: 'Client', icon: <Briefcase size={14} />, category: 'Basic' },
    { key: 'department', label: 'Department', icon: <Briefcase size={14} />, category: 'Basic' },
    
    // Status & Priority
    { key: 'jobStatus', label: 'Status', icon: <Shield size={14} />, category: 'Status' },
    { key: 'jobPriority', label: 'Priority', icon: <Award size={14} />, category: 'Status' },
    
    // Job Details
    { key: 'numberOfOpenings', label: 'Openings', icon: <Users size={14} />, category: 'Job Details' },
    { key: 'workModel', label: 'Work Model', icon: <MapPin size={14} />, category: 'Job Details' },
    { key: 'workLocation', label: 'Work Location', icon: <MapPin size={14} />, category: 'Job Details' },
    { key: 'workingHours', label: 'Working Hours', icon: <Clock size={14} />, category: 'Job Details' },
    { key: 'jobType', label: 'Job Type', icon: <Briefcase size={14} />, category: 'Job Details' },
    
    // Experience & Skills
    { key: 'experienceMin', label: 'Min Exp', icon: <Award size={14} />, category: 'Experience & Skills' },
    { key: 'experienceMax', label: 'Max Exp', icon: <Award size={14} />, category: 'Experience & Skills' },
    { key: 'primarySkills', label: 'Primary Skills', icon: <Award size={14} />, category: 'Experience & Skills' },
    { key: 'secondarySkills', label: 'Secondary Skills', icon: <Award size={14} />, category: 'Experience & Skills' },
    
    // Compensation
    { key: 'compensationMin', label: 'Min Salary', icon: <DollarSign size={14} />, category: 'Compensation' },
    { key: 'compensationMax', label: 'Max Salary', icon: <DollarSign size={14} />, category: 'Compensation' },
    { key: 'compensationCurrency', label: 'Currency', icon: <DollarSign size={14} />, category: 'Compensation' },
    { key: 'compensationPaytype', label: 'Pay Type', icon: <DollarSign size={14} />, category: 'Compensation' },
    { key: 'vendorCommercials', label: 'Vendor Comms', icon: <DollarSign size={14} />, category: 'Compensation' },
    
    // Education & Requirements
    { key: 'education', label: 'Education', icon: <FileText size={14} />, category: 'Requirements' },
    { key: 'preferredInstitution', label: 'Pref Institute', icon: <FileText size={14} />, category: 'Requirements' },
    { key: 'noticePeriod', label: 'Notice Period', icon: <Clock size={14} />, category: 'Requirements' },
    { key: 'workAuthorization', label: 'Work Auth', icon: <Shield size={14} />, category: 'Requirements' },
    { key: 'visaSponsorship', label: 'Visa Sponsor', icon: <Shield size={14} />, category: 'Requirements' },
    { key: 'backgroundCheck', label: 'BGC', icon: <Shield size={14} />, category: 'Requirements' },
    { key: 'securityClearance', label: 'Security Clear', icon: <Shield size={14} />, category: 'Requirements' },
    { key: 'licenseCertifications', label: 'Licenses', icon: <FileText size={14} />, category: 'Requirements' },
    
    // Management & Interview
    { key: 'hiringManagerName', label: 'Hiring Manager', icon: <Users size={14} />, category: 'Management' },
    { key: 'hiringManagerEmail', label: 'Manager Email', icon: <Users size={14} />, category: 'Management' },
    { key: 'assignedTo', label: 'Assigned To', icon: <Users size={14} />, category: 'Management' },
    { key: 'interviewMode', label: 'Interview Mode', icon: <Users size={14} />, category: 'Interview' },
    { key: 'interviewRounds', label: 'Int. Rounds', icon: <Users size={14} />, category: 'Interview' },
    
    // Metadata
    { key: 'createdAt', label: 'Created Date', icon: <Calendar size={14} />, category: 'Metadata' },
    { key: 'updatedAt', label: 'Updated Date', icon: <Calendar size={14} />, category: 'Metadata' },
    { key: 'createdBy', label: 'Created By', icon: <Users size={14} />, category: 'Metadata' },
    { key: 'updatedBy', label: 'Updated By', icon: <Users size={14} />, category: 'Metadata' }
  ];

  // Group columns by category
  const columnsByCategory = allColumns.reduce((acc, column) => {
    const category = column.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(column);
    return acc;
  }, {});

  // Get visible columns from localStorage or use default
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('jobViewColumns');
    if (saved) {
      const savedColumns = JSON.parse(saved);
      // Ensure required columns are always included
      const requiredColumns = allColumns.filter(col => col.required).map(col => col.key);
      return [...new Set([...requiredColumns, ...savedColumns])];
    }
    // Default visible columns
    return [
      'jobCode', 'jobTitle', 'client', 'jobStatus', 'jobPriority', 
      'numberOfOpenings', 'workModel', 'jobType', 'experienceMin', 
      'experienceMax', 'assignedTo', 'createdAt'
    ];
  });

  const [filters, setFilters] = useState({
    jobStatus: null,
    jobPriority: null,
    workModel: null,
    jobType: null,
    client: '',
    department: ''
  });

  const jobStatusOptions = [
    { value: 'OPEN', label: 'Open' },
    { value: 'CLOSED', label: 'Closed' },
    { value: 'ON_HOLD', label: 'On Hold' },
    { value: 'DRAFT', label: 'Draft' }
  ];

  const priorityOptions = [
    { value: 'HIGH', label: 'High' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LOW', label: 'Low' }
  ];

  const workModelOptions = [
    { value: 'REMOTE', label: 'Remote' },
    { value: 'ONSITE', label: 'On-site' },
    { value: 'HYBRID', label: 'Hybrid' }
  ];

  const jobTypeOptions = [
    { value: 'FULL_TIME', label: 'Full Time' },
    { value: 'PART_TIME', label: 'Part Time' },
    { value: 'CONTRACT', label: 'Contract' },
    { value: 'TEMPORARY', label: 'Temporary' },
    { value: 'INTERNSHIP', label: 'Internship' }
  ];

  // Fetch all jobs
  const fetchJobs = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const response = await jobsAPI.getAllJobs();
      let jobsData = Array.isArray(response.data) ? response.data : response.data.content || [];
      setJobs(jobsData);
      setFilteredJobs(jobsData);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
        toast.error('Access Denied: Syncing permissions...');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to fetch jobs: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...jobs];

    if (filters.jobStatus) {
      filtered = filtered.filter(j => j.jobStatus === filters.jobStatus.value);
    }

    if (filters.jobPriority) {
      filtered = filtered.filter(j => j.jobPriority === filters.jobPriority.value);
    }

    if (filters.workModel) {
      filtered = filtered.filter(j => j.workModel === filters.workModel.value);
    }

    if (filters.jobType) {
      filtered = filtered.filter(j => j.jobType === filters.jobType.value);
    }

    if (filters.client) {
      filtered = filtered.filter(j =>
        j.client?.toLowerCase().includes(filters.client.toLowerCase())
      );
    }

    if (filters.department) {
      filtered = filtered.filter(j =>
        j.department?.toLowerCase().includes(filters.department.toLowerCase())
      );
    }

    if (searchQuery) {
      filtered = filtered.filter(j =>
        j.jobCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        j.jobTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        j.client?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        j.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        j.hiringManagerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        j.assignedTo?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  }, [filters, jobs, searchQuery]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      jobStatus: null,
      jobPriority: null,
      workModel: null,
      jobType: null,
      client: '',
      department: ''
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredJobs.map(job => job.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleExport = () => {
    const visibleCols = allColumns.filter(col => visibleColumns.includes(col.key));
    exportJobsToExcel(filteredJobs, visibleCols);
  };

  const handleEdit = (job) => {
    setSelectedJobForEdit(job);
    setShowEditModal(true);
  };

  const handlePreview = (job) => {
    setSelectedJobForPreview(job);
    setShowPreviewModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-green-100 text-green-800',
      'CLOSED': 'bg-red-100 text-red-800',
      'ON_HOLD': 'bg-yellow-100 text-yellow-800',
      'DRAFT': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'HIGH': 'bg-red-100 text-red-800',
      'MEDIUM': 'bg-yellow-100 text-yellow-800',
      'LOW': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatSkills = (skills) => {
    if (!skills) return '-';
    if (Array.isArray(skills)) {
      return skills.slice(0, 2).join(', ') + (skills.length > 2 ? '...' : '');
    }
    return skills.length > 30 ? skills.substring(0, 30) + '...' : skills;
  };

  const handleColumnToggle = (columnKey) => {
    const column = allColumns.find(col => col.key === columnKey);
    if (column?.required) return;

    setVisibleColumns(prev => {
      const newColumns = prev.includes(columnKey)
        ? prev.filter(key => key !== columnKey)
        : [...prev, columnKey];
      
      // Save to localStorage
      localStorage.setItem('jobViewColumns', JSON.stringify(newColumns));
      return newColumns;
    });
  };

  const selectAllColumns = () => {
    const allColumnKeys = allColumns.map(col => col.key);
    setVisibleColumns(allColumnKeys);
    localStorage.setItem('jobViewColumns', JSON.stringify(allColumnKeys));
  };

  const resetToDefaultColumns = () => {
    const defaultColumns = [
      'jobCode', 'jobTitle', 'client', 'jobStatus', 'jobPriority', 
      'numberOfOpenings', 'workModel', 'jobType', 'experienceMin', 
      'experienceMax', 'assignedTo', 'createdAt'
    ];
    setVisibleColumns(defaultColumns);
    localStorage.setItem('jobViewColumns', JSON.stringify(defaultColumns));
  };

  const getColumnValue = (job, columnKey) => {
    const value = job[columnKey];

    switch (columnKey) {
      case 'jobTitle': {
        const title = job.jobTitle || '';
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: BRAND }}
            >
              {title ? title.charAt(0).toUpperCase() : '?'}
            </div>
            <button
              onClick={e => { e.stopPropagation(); navigate(`/ats/jobs/${job.id}`); }}
              className="text-sm font-medium hover:underline cursor-pointer"
              style={{ color: BRAND }}
              onMouseEnter={e => e.currentTarget.style.color = BRAND_DARK}
              onMouseLeave={e => e.currentTarget.style.color = BRAND}
            >
              {title || <span className="text-gray-400 italic">Untitled</span>}
            </button>
          </div>
        );
      }

      case 'jobStatus':
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(value)}`}>
            {value || '-'}
          </span>
        );
      
      case 'jobPriority':
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(value)}`}>
            {value || '-'}
          </span>
        );
      
      case 'createdAt':
      case 'updatedAt':
        return formatDate(value);
      
      case 'compensationMin':
      case 'compensationMax':
        if (!value) return '-';
        return `${job.compensationCurrency || ''} ${parseFloat(value).toLocaleString()}`;
      
      case 'experienceMin':
      case 'experienceMax':
        if (!value) return '-';
        return `${value} yrs`;
      
      case 'primarySkills':
      case 'secondarySkills':
        return formatSkills(value);
      
      case 'interviewRounds':
        if (!value) return '-';
        if (Array.isArray(value)) return `${value.length} rounds`;
        return value;
      
      default:
        return value || '-';
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Job Postings</h1>
            <p className="text-sm text-gray-500 mt-1">
              {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCustomizeView(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Columns size={16} />
              Customize View
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Download size={16} />
              Export
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm text-white rounded-md flex items-center gap-2"
              style={{ backgroundColor: "#1C4E6D" }}
            >
              <Plus size={16} />
              Create Job
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedRows.length > 0 && (
          <div className="mb-4 p-3 rounded-md flex items-center justify-between" style={{ backgroundColor: "#e8f4f9" }}>
            <span className="text-sm font-medium" style={{ color: "#1C4E6D" }}>
              {selectedRows.length} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-3 py-1 text-sm text-red-700 hover:bg-red-100 rounded"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedRows([])}
                className="p-1 rounded"
                style={{ color: "#1C4E6D" }}
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Status Filter */}
            <div className="w-40">
              <select
                value={filters.jobStatus?.value || ''}
                onChange={(e) => {
                  const selected = jobStatusOptions.find(s => s.value === e.target.value);
                  handleFilterChange('jobStatus', selected || null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              >
                <option value="">All Status</option>
                {jobStatusOptions.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div className="w-40">
              <select
                value={filters.jobPriority?.value || ''}
                onChange={(e) => {
                  const selected = priorityOptions.find(p => p.value === e.target.value);
                  handleFilterChange('jobPriority', selected || null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              >
                <option value="">All Priority</option>
                {priorityOptions.map(priority => (
                  <option key={priority.value} value={priority.value}>{priority.label}</option>
                ))}
              </select>
            </div>

            {/* Work Model Filter */}
            <div className="w-40">
              <select
                value={filters.workModel?.value || ''}
                onChange={(e) => {
                  const selected = workModelOptions.find(w => w.value === e.target.value);
                  handleFilterChange('workModel', selected || null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              >
                <option value="">All Work Models</option>
                {workModelOptions.map(model => (
                  <option key={model.value} value={model.value}>{model.label}</option>
                ))}
              </select>
            </div>

            {/* Job Type Filter */}
            <div className="w-40">
              <select
                value={filters.jobType?.value || ''}
                onChange={(e) => {
                  const selected = jobTypeOptions.find(t => t.value === e.target.value);
                  handleFilterChange('jobType', selected || null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              >
                <option value="">All Job Types</option>
                {jobTypeOptions.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Client Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Filter by client..."
                value={filters.client}
                onChange={(e) => handleFilterChange('client', e.target.value)}
                className="pl-10 pr-4 py-2 w-48 border border-gray-300 rounded-md text-sm"
              />
            </div>

            {/* Clear Filters */}
            {(filters.jobStatus || filters.jobPriority || filters.workModel || filters.jobType || filters.client) && (
              <button 
                onClick={clearAllFilters}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded-md"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Stats */}
      <div className="flex items-center justify-between p-4 border-b gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search jobs by code, title, client, department, or manager..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm"
            style={{ 
              boxShadow: "inset 0 0 0 2px #1C4E6D" 
            }}
            onFocus={(e) => e.target.style.boxShadow = "inset 0 0 0 2px #1C4E6D"}
            onBlur={(e) => e.target.style.boxShadow = "none"}
          />
        </div>
        
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <Briefcase size={14} />
            <span>{visibleColumns.length} columns</span>
          </span>
          <button
            onClick={() => setShowCustomizeView(true)}
            className="flex items-center gap-1"
            style={{ color: "#1C4E6D" }}
          >
            <Columns size={14} />
            <span>Customize</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {accessDenied ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-600">
            <p className="text-lg font-semibold">Access Denied</p>
            <p className="text-sm mt-2 text-center max-w-md">
              You do not have permission to view job postings. Your permissions are being synced from the server.
            </p>
            <button
              onClick={() => fetchJobs()}
              disabled={loading}
              className="mt-4 px-4 py-2 text-white rounded disabled:bg-gray-400"
              style={{ backgroundColor: "#1C4E6D" }}
            >
              {loading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: "#1C4E6D" }}></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <p className="text-lg font-medium">No jobs found</p>
            <p className="text-sm mt-1">Try adjusting your filters or create a new job posting</p>
            <button
              onClick={() => {
                clearAllFilters();
                setSearchQuery('');
              }}
              className="mt-3 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="w-12 px-4 py-3 text-left border-b">
                    <input
                      type="checkbox"
                      checked={selectedRows.length === filteredJobs.length && filteredJobs.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  {allColumns
                    .filter(col => visibleColumns.includes(col.key))
                    .map((column) => (
                      <th
                        key={column.key}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border-b"
                      >
                        <div className="flex items-center gap-2">
                          {column.icon}
                          <span>{column.label}</span>
                          <ArrowUpDown size={12} className="text-gray-400 cursor-pointer hover:text-gray-600" />
                        </div>
                      </th>
                    ))}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobs.map((job) => (
                  <tr
                    key={job.id}
                    className={`group hover:bg-gray-50 ${
                      selectedRows.includes(job.id) ? '': ''
                    }`}
                    style={selectedRows.includes(job.id) ? { backgroundColor: "#e8f4f9" } : {}}
                  >
                    <td className="px-4 py-3 border-b" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(job.id)}
                        onChange={() => handleRowSelect(job.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    {allColumns
                      .filter(col => visibleColumns.includes(col.key))
                      .map((column) => (
                        <td key={column.key} className="px-4 py-3 text-sm text-gray-900 border-b">
                          <div className="whitespace-nowrap">
                            {getColumnValue(job, column.key)}
                          </div>
                        </td>
                      ))}
                    <td className="px-4 py-4 whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50 transition-colors shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.04)] border-b" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <button
                          onClick={() => handlePreview(job)}
                          className="p-1.5 rounded transition-colors"
                          style={{ color: "#1C4E6D" }}
                          title="Preview job"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(job)}
                          className="p-1.5 rounded transition-colors"
                          style={{ color: "#1C4E6D" }}
                          title="Edit job"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRows([job.id]);
                            setShowDeleteModal(true);
                          }}
                          className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Delete job"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customize View Modal */}
      {showCustomizeView && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
          <div className="bg-white shadow-2xl w-full max-w-md h-full flex flex-col animate-slideInRight overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b flex-shrink-0 bg-white">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Customize View</h2>
                <p className="text-sm text-gray-500 mt-1">Select columns to display in table</p>
              </div>
              <button
                onClick={() => setShowCustomizeView(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Quick Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={selectAllColumns}
                    className="px-4 py-2 text-sm text-white rounded"
                    style={{ backgroundColor: "#1C4E6D" }}
                  >
                    Select All Columns
                  </button>
                  <button
                    onClick={resetToDefaultColumns}
                    className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Reset to Default
                  </button>
                </div>

                {/* Columns by Category */}
                {Object.entries(columnsByCategory).map(([category, columns]) => (
                  <div key={category} className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {columns.map((column) => (
                        <label
                          key={column.key}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                            column.required
                              ? 'border'
                              : 'hover:bg-gray-50'
                          }`}
                          style={column.required ? { backgroundColor: "#e8f4f9", borderColor: "#1C4E6D" } : {}}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded bg-gray-100">
                              {column.icon}
                            </div>
                            <div>
                              <span className="font-medium text-gray-900">{column.label}</span>
                              {column.required && (
                                <span className="ml-2 text-xs font-medium" style={{ color: "#1C4E6D" }}>Required</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center">
                            {column.required ? (
                              <div className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: "#1C4E6D" }}>
                                <Check size={14} className="text-white" />
                              </div>
                            ) : (
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes(column.key)}
                                onChange={() => handleColumnToggle(column.key)}
                                className="w-5 h-5 rounded border-gray-300"
                                style={{ accentColor: "#1C4E6D" }}
                              />
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setShowCustomizeView(false)}
                className="flex-1 px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowCustomizeView(false)}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
              >
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateJobModal
          onClose={() => setShowCreateModal(false)}
          onCreate={() => {
            setShowCreateModal(false);
            fetchJobs();
          }}
        />
      )}

      {showEditModal && selectedJobForEdit && (
        <EditJobModal
          job={selectedJobForEdit}
          onClose={() => {
            setShowEditModal(false);
            setSelectedJobForEdit(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setSelectedJobForEdit(null);
            fetchJobs();
          }}
        />
      )}

      {showPreviewModal && selectedJobForPreview && (
        <JobPreview
          job={selectedJobForPreview}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedJobForPreview(null);
          }}
        />
      )}



{showDeleteModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Confirm Delete</h2>
        <button
          onClick={() => {
            console.log('❌ Cancel delete clicked');
            setShowDeleteModal(false);
            setSelectedRows([]);
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>
      </div>
      
      <p className="text-gray-600 mb-6">
        Are you sure you want to delete <strong>{selectedRows.length}</strong> job(s)? 
        This action cannot be undone.
      </p>
      
      {selectedRows.length === 1 && (
        <div className="bg-gray-50 p-3 rounded mb-4">
          <p className="text-sm text-gray-700">
            <strong>Job:</strong> {jobs.find(j => j.id === selectedRows[0])?.jobCode || 'Unknown'}
          </p>
        </div>
      )}
      
      <div className="flex gap-3">
        <button
          onClick={() => {
            console.log('❌ Cancel clicked');
            setShowDeleteModal(false);
            setSelectedRows([]);
          }}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            console.log('✅ Confirm delete clicked');
            console.log('Jobs to delete:', selectedRows);
            
            setLoading(true);
            try {
              if (selectedRows.length === 1) {
                console.log('Calling deleteJob with ID:', selectedRows[0]);
                await jobsAPI.deleteJob(selectedRows[0]);
                toast.success('Job deleted successfully');
              } else {
                console.log('Calling bulkDeleteJobs with IDs:', selectedRows);
                await jobsAPI.bulkDeleteJobs(selectedRows);
                toast.success(`${selectedRows.length} jobs deleted successfully`);
              }
              
              setSelectedRows([]);
              setShowDeleteModal(false);
              await fetchJobs();
              
            } catch (error) {
              console.error('❌ Delete error:', error);
              console.error('Error details:', {
                status: error.response?.status,
                message: error.response?.data?.message,
                data: error.response?.data
              });
              
              if (error.response?.status === 403) {
                toast.error('Access Denied: You do not have permission to delete jobs');
              } else {
                toast.error('Delete failed: ' + (error.response?.data?.message || error.message));
              }
            } finally {
              setLoading(false);
            }
          }}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Deleting...
            </span>
          ) : (
            'Delete'
          )}
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default Jobs;